import { Component, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCalloutComponent } from '@ui/callout/callout.component';
import { VxCheckboxComponent } from '@ui/checkbox/checkbox.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxInputComponent } from '@ui/input/input.component';
import { Apollo, gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { ProductAnalyticsService } from 'src/app/services/analytics/product-analytics.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { LoadOrganizationAction } from 'src/app/store/organization/organization.actions';
import { OrganizationStateModule } from 'src/app/store/organization/organization.state.module';
import { SettingUpdateOrganizationMemberMeAction } from 'src/app/store/settings/settings.actions';
import { LoadSubscriptionAction } from 'src/app/store/subscription/subscription.actions';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';
import {
  LoadUserAction,
  SelectOrganizationAction,
} from 'src/app/store/user/user.actions';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthPanelComponent } from '../components/auth-panel/auth-panel.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';

const CREATE_ORGANIZATION_MUTATION = gql`
  mutation CreateOrganization($name: String!) {
    createOrganization(newOrganization: { name: $name }) {
      id
    }
  }
`;

/**
 * Another organization for someone who has one already (the first one is
 * created in onboarding).
 */
@Component({
  selector: 'app-page-new-organization',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    OrganizationStateModule,
    AuthHeaderComponent,
    AuthPanelComponent,
    AuthShellComponent,
    VxButtonComponent,
    VxCalloutComponent,
    VxCheckboxComponent,
    VxFormFieldComponent,
    VxIconComponent,
    VxInputComponent,
  ],
  templateUrl: './new-organization.component.html',
})
export class NewOrganizationPageComponent {
  private apollo = inject(Apollo);
  private router = inject(Router);
  private store = inject(Store);
  private translateService = inject(TranslationService);
  private analytics = inject(ProductAnalyticsService);

  protected readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    acceptTerms: new FormControl(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue],
    }),
  });

  protected readonly isSubmitting = signal(false);

  protected readonly failed = signal(false);

  protected create(): void {
    if (this.form.invalid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.failed.set(false);
    this.isSubmitting.set(true);

    this.apollo
      .mutate<{ createOrganization: { id: string } }>({
        mutation: CREATE_ORGANIZATION_MUTATION,
        variables: {
          name: this.form.controls.name.value.trim(),
        },
      })
      .subscribe({
        next: res => {
          const actions = [
            new SelectOrganizationAction(res.data!.createOrganization.id),
            new LoadUserAction(),
            new LoadOrganizationAction(),
            new SettingUpdateOrganizationMemberMeAction(
              this.translateService.currentTimezone,
              this.translateService.currentLocale
            ),
          ];

          // Create a timer that resolves after 3 seconds
          const timer = new Promise(resolve => setTimeout(resolve, 3000));

          const checkSubscription = async (
            attempt = 1,
            delay = 500
          ): Promise<void> => {
            if (attempt > 10) {
              return;
            }

            await this.store.dispatch(new LoadSubscriptionAction()).toPromise();
            const canChange = this.store.selectSnapshot(
              SubscriptionState.canChangeSubscription
            );

            if (!canChange) {
              await new Promise(resolve => setTimeout(resolve, delay));
              return checkSubscription(attempt + 1, delay * 2);
            }
          };

          // Wait for both the actions and the timer to complete
          Promise.all([
            firstValueFrom(this.store.dispatch(actions)),
            checkSubscription(),
            timer,
          ]).then(() => {
            this.analytics.capture('organization.created', {
              source: 'switcher',
            });
            this.router.navigate(['/dashboard']);
          });
        },
        error: () => {
          this.isSubmitting.set(false);
          this.failed.set(true);
        },
      });
  }
}
